import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, ArrowRight, Loader2, Check, Sparkles, FlaskConical, ShieldCheck } from 'lucide-react';
import Seo from '../components/Seo';
import useReveal from '../hooks/useReveal';
import { useLang } from '../context/LanguageContext';
import { getDopamineCopy } from '../i18n/dopamine';
import { track } from '../components/landingUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const A = 'https://www.dopamine.org.tr/assets';

const WaitlistForm = ({ c, dark, idSuffix }) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setState('error'); setMsg(c.invalid); return; }
    setState('loading');
    try {
      const res = await axios.post(`${API}/dopamine/waitlist`, { email: email.trim() });
      setState('done');
      setMsg(res.data.already ? c.already : c.ok);
      if (!res.data.already) track('dopamine_waitlist_signup', { page: 'dopamine' });
    } catch {
      setState('error'); setMsg(c.invalid);
    }
  };

  if (state === 'done') {
    return (
      <div className={`flex items-center gap-3 px-6 py-4 border ${dark ? 'border-[#aebf9f]/40 text-[#cfd9c3]' : 'border-[#44503f]/30 text-[#44503f]'}`} data-testid={`waitlist-success-${idSuffix}`}>
        <Check className="w-5 h-5 text-[#aebf9f]" /><span className="text-sm">{msg}</span>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="w-full max-w-md">
      <div className={`flex border ${dark ? 'border-[#aebf9f]/40 bg-white/5 backdrop-blur-md' : 'border-[#44503f]/30 bg-white'}`}>
        <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
          placeholder={c.ph} data-testid={`waitlist-email-${idSuffix}`}
          className={`flex-1 min-w-0 bg-transparent px-5 py-4 text-sm focus:outline-none ${dark ? 'text-ivory placeholder-ivory/40' : 'text-[#141c14] placeholder-[#7b8674]'}`} />
        <button type="submit" disabled={state === 'loading'} data-testid={`waitlist-submit-${idSuffix}`}
          className="shrink-0 inline-flex items-center gap-2 px-6 py-4 bg-[#aebf9f] text-[#141c14] text-[0.68rem] tracking-[0.2em] uppercase font-semibold hover:bg-[#cfd9c3] transition-colors disabled:opacity-60">
          {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} {c.btn}
        </button>
      </div>
      {state === 'error' && <p className="mt-2 text-xs text-red-400" data-testid={`waitlist-error-${idSuffix}`}>{msg}</p>}
    </form>
  );
};

export default function DopamineLaunch() {
  const { lang } = useLang();
  const c = getDopamineCopy(lang);
  useReveal(lang);
  useEffect(() => { track('landing_page_view', { page: 'dopamine' }); }, []);

  return (
    <div className="bg-[#141c14]">
      <Seo title="DOPAMINE — Luxury Exosome-Inspired Scalp Serum | Coming Soon"
        description="DOPAMINE by ARGE Cosmetics: a luxury exosome-inspired scalp serum for refined daily root care. Launching soon in the UK — join the waitlist."
        path="/dopamine"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            { '@type': 'Brand', name: 'DOPAMINE', url: 'https://argecosmetics.co.uk/dopamine', parentOrganization: { '@type': 'Organization', name: 'ARGE Cosmetics' } },
            { '@type': 'FAQPage', mainEntity: c.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
          ],
        }} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#141c14]" />
        <div className="absolute -top-40 -right-40 w-[40rem] h-[40rem] rounded-full bg-[#aebf9f]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-3 text-[0.68rem] tracking-[0.4em] uppercase text-[#aebf9f]"><span className="w-10 h-px bg-[#aebf9f]" />{c.kicker}</span>
            <p className="mt-8 font-serif text-[0.95rem] tracking-[0.55em] uppercase text-ivory/70">D O P A M I N E</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.08]">{c.h1a} <span className="italic text-[#cfd9c3]">{c.h1b}</span></h1>
            <p className="mt-6 text-lg text-ivory/70 leading-relaxed max-w-lg">{c.sub}</p>
            <div className="mt-10"><WaitlistForm c={c} dark idSuffix="hero" /></div>
          </div>
          <div className="reveal relative">
            <div className="absolute inset-6 border border-[#aebf9f]/20" />
            <img src={`${A}/product-duo.png`} alt="DOPAMINE 30ml and 80ml scalp serums" className="relative w-full object-cover" loading="eager" />
          </div>
        </div>
      </section>

      {/* STATEMENT STRIP */}
      <div className="border-y border-[#aebf9f]/20 bg-[#101710] overflow-hidden py-5">
        <p className="text-center font-serif italic text-xl md:text-2xl text-[#cfd9c3] tracking-wide">{c.stK}</p>
      </div>

      {/* INGREDIENTS */}
      <section className="py-20 md:py-28 bg-[#f4f2ea]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 reveal">
              <span className="inline-flex items-center gap-3 text-[0.68rem] tracking-[0.35em] uppercase text-[#44503f]"><FlaskConical className="w-4 h-4" />{c.ingK}</span>
              <h2 className="mt-4 font-serif text-3xl md:text-5xl text-[#141c14] leading-tight">{c.ingT}</h2>
              <img src={`${A}/ingredient-texture-1.png`} alt="DOPAMINE ingredient texture" loading="lazy" className="mt-8 w-full h-56 object-cover" />
            </div>
            <div className="lg:col-span-3 divide-y divide-[#44503f]/15">
              {c.ings.map((ing, i) => (
                <div key={ing.t} className="reveal flex gap-6 py-6" data-testid={`dopamine-ingredient-${i}`}>
                  <span className="font-serif text-3xl text-[#aebf9f] w-12 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-[#141c14]">{ing.t}</h3>
                    <p className="mt-1.5 text-sm text-[#44503f] leading-relaxed">{ing.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="py-20 md:py-28 bg-[#141c14]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <span className="text-[0.68rem] tracking-[0.35em] uppercase text-[#aebf9f]">{c.fmtK}</span>
            <h2 className="mt-4 font-serif text-3xl md:text-5xl text-ivory">{c.fmtT}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[{ img: `${A}/product-30ml.png`, t: c.f30t, d: c.f30d, to: '/shop/dopamine-scalp-serum-30', ml: '30' },
              { img: `${A}/hero-product.png`, t: c.f80t, d: c.f80d, to: '/shop/dopamine-scalp-serum-80', ml: '80' }].map((f) => (
              <div key={f.ml} className="reveal group bg-[#101710] border border-[#aebf9f]/15 hover:border-[#aebf9f]/40 transition-colors" data-testid={`dopamine-format-${f.ml}`}>
                <div className="h-80 overflow-hidden">
                  <img src={f.img} alt={f.t} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8">
                  <p className="font-serif text-5xl text-[#aebf9f]/40">{f.ml}<span className="text-xl">ml</span></p>
                  <h3 className="mt-2 font-serif text-2xl text-ivory">{f.t}</h3>
                  <p className="mt-2 text-sm text-ivory/60">{f.d}</p>
                  <Link to={f.to} className="mt-5 inline-flex items-center gap-2 text-[0.68rem] tracking-[0.2em] uppercase text-[#aebf9f] hover:text-[#cfd9c3] transition-colors">{c.view} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BY ARGE */}
      <section className="py-20 bg-[#f4f2ea]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center reveal">
          <ShieldCheck className="w-8 h-8 text-[#44503f] mx-auto mb-4" />
          <span className="text-[0.68rem] tracking-[0.35em] uppercase text-[#44503f]">{c.byK}</span>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl text-[#141c14]">{c.byT}</h2>
          <p className="mt-5 text-[#44503f] leading-relaxed">{c.byP}</p>
          <p className="mt-6 text-xs tracking-[0.25em] uppercase text-[#7b8674]">GMP · ISO 22716 · ISO 9001 · ISO 14001 · ISO 45001</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 bg-[#141c14]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-3xl md:text-4xl text-ivory text-center mb-10 reveal">{c.faqT}</h2>
          <div className="space-y-4">
            {c.faqs.map((f) => (
              <div key={f.q} className="reveal bg-[#101710] border-l-2 border-[#aebf9f] p-6" data-testid="dopamine-faq-item">
                <h3 className="font-serif text-xl text-[#cfd9c3]">{f.q}</h3>
                <p className="mt-2 text-sm text-ivory/65 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 bg-[#101710] border-t border-[#aebf9f]/20">
        <div className="max-w-2xl mx-auto px-6 text-center reveal">
          <Sparkles className="w-7 h-7 text-[#aebf9f] mx-auto mb-5" />
          <h2 className="font-serif text-4xl md:text-5xl text-ivory">{c.finT}</h2>
          <p className="mt-4 text-ivory/65">{c.finP}</p>
          <div className="mt-9 flex justify-center"><WaitlistForm c={c} dark idSuffix="footer" /></div>
        </div>
      </section>
    </div>
  );
}
